import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import maplibregl, { Map as MapLibreMap, Marker, LngLatBounds } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { devicesApi, geocodingApi } from '../services/api';
import { useDevicesStore, Device } from '../store/devices.store';
import { useTenantContextStore } from '../store/tenant-context.store';
import { useSettingsStore, MapStyle } from '../store/settings.store';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Clock,
  MapPin,
  Gauge,
  Route,
  Calendar,
  Flag,
  Navigation,
  Loader2,
  ChevronLeft,
} from 'lucide-react';
import clsx from 'clsx';
import { useBreakpoint } from '../hooks/useBreakpoint';

interface Position {
  id: string;
  latitude: number;
  longitude: number;
  speed: number;
  heading: number;
  timestamp: string;
  ignition?: boolean;
}

interface Trip {
  deviceId: string;
  deviceName: string;
  startTime: string;
  endTime: string;
  startLatitude: number;
  startLongitude: number;
  endLatitude: number;
  endLongitude: number;
  distance: number; // in km from backend
  duration: number; // in ms from backend
  maxSpeed: number;
  avgSpeed: number;
  positionCount: number;
  partLabel?: string; // e.g. "1/2", "2/2" for split trips
  // Optional - populated by geocoding
  startAddress?: string;
  endAddress?: string;
}

// `label` is an i18n key resolved at render time.
const datePresets = [
  { label: 'history.preset_today', getValue: () => ({ start: startOfDay(new Date()), end: endOfDay(new Date()) }) },
  { label: 'history.preset_yesterday', getValue: () => ({ start: startOfDay(subDays(new Date(), 1)), end: endOfDay(subDays(new Date(), 1)) }) },
  { label: 'history.preset_7d', getValue: () => ({ start: startOfDay(subDays(new Date(), 7)), end: endOfDay(new Date()) }) },
  { label: 'history.preset_30d', getValue: () => ({ start: startOfDay(subDays(new Date(), 30)), end: endOfDay(new Date()) }) },
];

// Speed color gradient
function getSpeedColor(speed: number, maxSpeed: number): string {
  const ratio = Math.min(speed / Math.max(maxSpeed, 1), 1);
  if (ratio < 0.33) return '#10B981'; // green
  if (ratio < 0.66) return '#F59E0B'; // amber
  return '#EF4444'; // red
}

// Map style configuration
function getMapStyle(style: MapStyle): maplibregl.StyleSpecification {
  const styles: Record<MapStyle, maplibregl.StyleSpecification> = {
    'osm': {
      version: 8,
      sources: {
        'raster-tiles': {
          type: 'raster',
          tiles: [
            'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
            'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
            'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
          ],
          tileSize: 256,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        },
      },
      layers: [{ id: 'raster-layer', type: 'raster', source: 'raster-tiles', minzoom: 0, maxzoom: 19 }],
    },
    'osm-humanitarian': {
      version: 8,
      sources: {
        'raster-tiles': {
          type: 'raster',
          tiles: ['https://a.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png'],
          tileSize: 256,
          attribution: '&copy; OpenStreetMap contributors, Tiles: HOT',
        },
      },
      layers: [{ id: 'raster-layer', type: 'raster', source: 'raster-tiles', minzoom: 0, maxzoom: 19 }],
    },
    'google-streets': {
      version: 8,
      sources: {
        'raster-tiles': {
          type: 'raster',
          tiles: ['https://mt0.google.com/vt/lyrs=m&hl=fr&x={x}&y={y}&z={z}'],
          tileSize: 256,
          attribution: '&copy; Google Maps',
        },
      },
      layers: [{ id: 'raster-layer', type: 'raster', source: 'raster-tiles', minzoom: 0, maxzoom: 20 }],
    },
    'google-traffic': {
      version: 8,
      sources: {
        'raster-tiles': {
          type: 'raster',
          tiles: ['https://mt0.google.com/vt/lyrs=m,traffic&hl=fr&x={x}&y={y}&z={z}&s=Ga'],
          tileSize: 256,
          attribution: '&copy; Google Maps',
        },
      },
      layers: [{ id: 'raster-layer', type: 'raster', source: 'raster-tiles', minzoom: 0, maxzoom: 20 }],
    },
    'google-satellite': {
      version: 8,
      sources: {
        'raster-tiles': {
          type: 'raster',
          tiles: ['https://mt0.google.com/vt/lyrs=s&hl=fr&x={x}&y={y}&z={z}'],
          tileSize: 256,
          attribution: '&copy; Google Maps',
        },
      },
      layers: [{ id: 'raster-layer', type: 'raster', source: 'raster-tiles', minzoom: 0, maxzoom: 20 }],
    },
    'google-hybrid': {
      version: 8,
      sources: {
        'raster-tiles': {
          type: 'raster',
          tiles: ['https://mt0.google.com/vt/lyrs=y&hl=fr&x={x}&y={y}&z={z}'],
          tileSize: 256,
          attribution: '&copy; Google Maps',
        },
      },
      layers: [{ id: 'raster-layer', type: 'raster', source: 'raster-tiles', minzoom: 0, maxzoom: 20 }],
    },
  };
  return styles[style] || styles['osm'];
}

export default function HistoryPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const devices = useDevicesStore((state) => state.devices);
  const setDevices = useDevicesStore((state) => state.setDevices);
  const { selectedTenant } = useTenantContextStore();
  const tenantKey = selectedTenant?.id || 'default';
  const { mapStyle } = useSettingsStore();
  const { isMobile } = useBreakpoint();
  const [mobilePanel, setMobilePanel] = useState<'selection' | 'map'>('selection');

  // State
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>(searchParams.get('device') || '');
  const [dateRange, setDateRange] = useState(() => {
    const preset = datePresets[0].getValue();
    return { start: preset.start, end: preset.end };
  });
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [playbackIndex, setPlaybackIndex] = useState(0);

  // Map refs
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<MapLibreMap | null>(null);
  const mapLoaded = useRef<boolean>(false);
  const playbackMarker = useRef<Marker | null>(null);
  const startMarker = useRef<Marker | null>(null);
  const endMarker = useRef<Marker | null>(null);
  const playbackIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch devices if not loaded — push them into the zustand store so the
  // vehicle picker populates when the user lands directly on /history.
  const { data: devicesQuery } = useQuery({
    queryKey: ['devices', tenantKey],
    queryFn: async () => {
      const response = await devicesApi.list({ limit: 1000 });
      return response.data;
    },
    enabled: devices.length === 0,
  });

  useEffect(() => {
    const list = (devicesQuery as { data?: Device[] } | undefined)?.data;
    if (list && list.length > 0) {
      setDevices(list);
    }
  }, [devicesQuery, setDevices]);

  // Fetch positions for selected device and date range
  const { data: positionsData, isLoading: positionsLoading } = useQuery({
    queryKey: ['positions', selectedDeviceId, dateRange.start, dateRange.end, tenantKey],
    queryFn: async () => {
      const response = await devicesApi.getPositions(
        selectedDeviceId,
        dateRange.start.toISOString(),
        dateRange.end.toISOString()
      );
      return response.data;
    },
    enabled: !!selectedDeviceId,
  });

  // Fetch trips
  const { data: tripsData, isLoading: tripsLoading } = useQuery({
    queryKey: ['trips', selectedDeviceId, dateRange.start, dateRange.end, tenantKey],
    queryFn: async () => {
      const response = await devicesApi.getTrips(
        selectedDeviceId,
        dateRange.start.toISOString(),
        dateRange.end.toISOString()
      );
      return response.data;
    },
    enabled: !!selectedDeviceId,
  });

  const allPositions: Position[] = useMemo(
    () => Array.isArray(positionsData?.data) ? positionsData.data : [],
    [positionsData?.data]
  );
  const trips: Trip[] = useMemo(
    () => Array.isArray(tripsData?.data) ? tripsData.data : [],
    [tripsData?.data]
  );

  // State for geocoded addresses
  const [tripAddresses, setTripAddresses] = useState<Map<string, { start: string; end: string }>>(new Map());

  // Fetch addresses for trips (async)
  useEffect(() => {
    if (trips.length === 0) {
      setTripAddresses(new Map());
      return;
    }

    // Collect unique coordinates from trips
    const coordinates: Array<{ lat: number; lng: number }> = [];
    const coordToTripMap: Array<{ tripKey: string; type: 'start' | 'end' }> = [];

    for (const trip of trips) {
      const tripKey = trip.startTime;

      // Start location
      coordinates.push({ lat: trip.startLatitude, lng: trip.startLongitude });
      coordToTripMap.push({ tripKey, type: 'start' });

      // End location (if available)
      if (trip.endLatitude && trip.endLongitude) {
        coordinates.push({ lat: trip.endLatitude, lng: trip.endLongitude });
        coordToTripMap.push({ tripKey, type: 'end' });
      }
    }

    // Batch geocode (limited to 50 coordinates per request)
    const fetchAddresses = async () => {
      const newAddresses = new Map<string, { start: string; end: string }>();

      // Process in batches of 50
      for (let i = 0; i < coordinates.length; i += 50) {
        const batch = coordinates.slice(i, i + 50);
        const batchMap = coordToTripMap.slice(i, i + 50);

        try {
          const response = await geocodingApi.batch(batch);
          const addresses = response.data.addresses;

          // Map addresses back to trips
          batch.forEach((coord, idx) => {
            const key = `${coord.lat.toFixed(5)},${coord.lng.toFixed(5)}`;
            const address = addresses[key];
            const mapping = batchMap[idx];

            if (address && mapping) {
              const existing = newAddresses.get(mapping.tripKey) || { start: '', end: '' };
              existing[mapping.type] = address;
              newAddresses.set(mapping.tripKey, existing);
            }
          });
        } catch (error) {
          console.error('Failed to batch geocode:', error);
        }
      }

      setTripAddresses(newAddresses);
    };

    fetchAddresses();
  }, [trips]);

  // Filter positions based on selected trip - memoized to avoid infinite loops
  // Ne retourner des positions que si un trajet est sélectionné
  const positions: Position[] = useMemo(() => {
    if (!selectedTrip) return [];

    const startTime = new Date(selectedTrip.startTime).getTime();
    const endTime = selectedTrip.endTime
      ? new Date(selectedTrip.endTime).getTime()
      : Date.now();

    return allPositions.filter((p) => {
      const posTime = new Date(p.timestamp).getTime();
      return posTime >= startTime && posTime <= endTime;
    });
  }, [allPositions, selectedTrip]);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: getMapStyle(mapStyle),
      center: [2.3522, 48.8566],
      zoom: 6,
      attributionControl: false,
    });

    map.current.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right');

    map.current.on('load', () => {
      console.log('History map loaded');
      mapLoaded.current = true;
    });

    map.current.on('error', (e) => {
      console.error('History map error:', e);
    });

    return () => {
      // Clean up markers before removing map
      if (playbackMarker.current) {
        playbackMarker.current.remove();
        playbackMarker.current = null;
      }
      if (startMarker.current) {
        startMarker.current.remove();
        startMarker.current = null;
      }
      if (endMarker.current) {
        endMarker.current.remove();
        endMarker.current = null;
      }
      // Clean up route layers/sources
      const layerIds = ['route-line-green', 'route-line-amber', 'route-line-red', 'route-line-bg'];
      layerIds.forEach((id) => {
        try {
          if (map.current?.getLayer(id)) map.current.removeLayer(id);
          if (map.current?.getSource(id)) map.current.removeSource(id);
        } catch { /* map may already be removed */ }
      });
      // Stop playback
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
        playbackIntervalRef.current = null;
      }
      map.current?.remove();
      map.current = null;
      mapLoaded.current = false;
    };
  }, []);

  // Update map style when it changes (skip initial render)
  const initialMount = useRef(true);
  useEffect(() => {
    if (!map.current || !mapLoaded.current) return;
    // Skip the first render (style already set in map constructor)
    if (initialMount.current) {
      initialMount.current = false;
      return;
    }
    map.current.setStyle(getMapStyle(mapStyle));
  }, [mapStyle]);

  // Douglas-Peucker simplification preserving shape (virages)
  // Seuil relevé à 5000 (les trips sont maintenant découpés côté backend)
  const simplifyPositions = useCallback((pts: Position[]): Position[] => {
    if (pts.length <= 5000) return pts;

    // Perpendicular distance from point to line segment
    const perpDist = (p: Position, a: Position, b: Position): number => {
      const dx = b.longitude - a.longitude;
      const dy = b.latitude - a.latitude;
      const lenSq = dx * dx + dy * dy;
      if (lenSq === 0) return Math.sqrt((p.longitude - a.longitude) ** 2 + (p.latitude - a.latitude) ** 2);
      let t = ((p.longitude - a.longitude) * dx + (p.latitude - a.latitude) * dy) / lenSq;
      t = Math.max(0, Math.min(1, t));
      const projX = a.longitude + t * dx;
      const projY = a.latitude + t * dy;
      return Math.sqrt((p.longitude - projX) ** 2 + (p.latitude - projY) ** 2);
    };

    // Iterative Douglas-Peucker (avoids stack overflow on large arrays)
    const douglasPeucker = (points: Position[], epsilon: number): boolean[] => {
      const keep = new Array(points.length).fill(false);
      keep[0] = true;
      keep[points.length - 1] = true;

      const stack: [number, number][] = [[0, points.length - 1]];
      while (stack.length > 0) {
        const [start, end] = stack.pop()!;
        let maxDist = 0;
        let maxIdx = start;
        for (let i = start + 1; i < end; i++) {
          const d = perpDist(points[i], points[start], points[end]);
          if (d > maxDist) { maxDist = d; maxIdx = i; }
        }
        if (maxDist > epsilon) {
          keep[maxIdx] = true;
          if (maxIdx - start > 1) stack.push([start, maxIdx]);
          if (end - maxIdx > 1) stack.push([maxIdx, end]);
        }
      }
      return keep;
    };

    // Also keep points with significant speed change or heading change
    const importantPoints = new Set<number>([0, pts.length - 1]);
    for (let i = 1; i < pts.length; i++) {
      const speedDelta = Math.abs(pts[i].speed - pts[i - 1].speed);
      const headingDelta = Math.abs((pts[i].heading || 0) - (pts[i - 1].heading || 0));
      if (speedDelta > 20 || (headingDelta > 30 && headingDelta < 330)) {
        importantPoints.add(i);
      }
    }

    // Epsilon doux — on préserve la forme de la route
    const epsilon = 0.00003; // ~3m at equator

    const keep = douglasPeucker(pts, epsilon);
    for (const idx of importantPoints) keep[idx] = true;

    const result = pts.filter((_, i) => keep[i]);
    // Fallback uniquement si > 8000 points (très rare avec le split backend à 5000)
    if (result.length > 8000) {
      const keep2 = douglasPeucker(pts, epsilon * 2);
      for (const idx of importantPoints) keep2[idx] = true;
      return pts.filter((_, i) => keep2[i]);
    }
    return result;
  }, []);

  // Draw route on map with speed-based coloring (optimized)
  const drawRoute = useCallback(() => {
    if (!map.current) return;

    // Remove existing layers
    ['route-line-green', 'route-line-amber', 'route-line-red', 'route-line-bg'].forEach((id) => {
      if (map.current?.getLayer(id)) map.current.removeLayer(id);
      if (map.current?.getSource(id)) map.current.removeSource(id);
    });

    // Remove existing start/end markers
    if (startMarker.current) { startMarker.current.remove(); startMarker.current = null; }
    if (endMarker.current) { endMarker.current.remove(); endMarker.current = null; }

    if (positions.length < 2) return;

    const maxSpeed = Math.max(...positions.map((p) => p.speed), 60);

    // Simplify positions for better performance
    const displayPositions = simplifyPositions(positions);
    console.log(`Drawing route: ${positions.length} positions simplified to ${displayPositions.length}`);

    // Group segments by color for better performance
    const greenSegments: [number, number][][] = [];
    const amberSegments: [number, number][][] = [];
    const redSegments: [number, number][][] = [];

    let currentSegment: [number, number][] = [[displayPositions[0].longitude, displayPositions[0].latitude]];
    let currentColor = getSpeedColor(displayPositions[0].speed, maxSpeed);

    for (let i = 1; i < displayPositions.length; i++) {
      const pos = displayPositions[i];
      const color = getSpeedColor(pos.speed, maxSpeed);

      if (color === currentColor) {
        currentSegment.push([pos.longitude, pos.latitude]);
      } else {
        // Save current segment and start new one
        currentSegment.push([pos.longitude, pos.latitude]);
        if (currentColor === '#10B981') greenSegments.push(currentSegment);
        else if (currentColor === '#F59E0B') amberSegments.push(currentSegment);
        else redSegments.push(currentSegment);

        currentSegment = [[pos.longitude, pos.latitude]];
        currentColor = color;
      }
    }

    // Don't forget the last segment
    if (currentSegment.length > 1) {
      if (currentColor === '#10B981') greenSegments.push(currentSegment);
      else if (currentColor === '#F59E0B') amberSegments.push(currentSegment);
      else redSegments.push(currentSegment);
    }

    // Add layers for each color group
    const addColorLayer = (id: string, segments: [number, number][][], color: string) => {
      if (segments.length === 0) return;

      map.current!.addSource(id, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: segments.map(coords => ({
            type: 'Feature' as const,
            properties: {},
            geometry: {
              type: 'LineString' as const,
              coordinates: coords,
            },
          })),
        },
      });

      map.current!.addLayer({
        id,
        type: 'line',
        source: id,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': color,
          'line-width': 4,
          'line-opacity': 0.9,
        },
      });
    };

    addColorLayer('route-line-green', greenSegments, '#10B981');
    addColorLayer('route-line-amber', amberSegments, '#F59E0B');
    addColorLayer('route-line-red', redSegments, '#EF4444');

    // Fit bounds
    const bounds = new LngLatBounds();
    positions.forEach((p) => bounds.extend([p.longitude, p.latitude]));
    map.current.fitBounds(bounds, { padding: 80, maxZoom: 16 });

    // Add start marker
    const startEl = document.createElement('div');
    startEl.innerHTML = `
      <div class="flex items-center justify-center w-8 h-8 bg-emerald-500 rounded-full border-2 border-white shadow-lg">
        <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/>
        </svg>
      </div>
    `;
    startMarker.current = new Marker({ element: startEl })
      .setLngLat([positions[0].longitude, positions[0].latitude])
      .addTo(map.current);

    // Add end marker
    const endEl = document.createElement('div');
    endEl.innerHTML = `
      <div class="flex items-center justify-center w-8 h-8 bg-red-500 rounded-full border-2 border-white shadow-lg">
        <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clip-rule="evenodd"/>
        </svg>
      </div>
    `;
    const lastPos = positions[positions.length - 1];
    endMarker.current = new Marker({ element: endEl })
      .setLngLat([lastPos.longitude, lastPos.latitude])
      .addTo(map.current);
  }, [positions, simplifyPositions]);

  // Draw route when positions change
  useEffect(() => {
    if (!map.current) return;

    if (map.current.loaded()) {
      drawRoute();
    } else {
      map.current.on('load', drawRoute);
    }

    return () => {
      // Remove listener to prevent stale callbacks on unmount/re-render
      map.current?.off('load', drawRoute);
    };
  }, [drawRoute]);

  // Create playback marker element
  const createPlaybackMarkerElement = useCallback(() => {
    const el = document.createElement('div');
    el.className = 'playback-marker';
    // Arrow points UP (North = 0°) by default, MapLibre rotation will orient it based on heading
    el.innerHTML = `
      <div style="position: relative;">
        <div style="width: 32px; height: 32px; background: #3B82F6; border-radius: 50%; border: 3px solid white; box-shadow: 0 4px 12px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
          <svg style="width: 14px; height: 14px;" viewBox="0 0 24 24" fill="white">
            <path d="M12 2L4 20h16L12 2z"/>
          </svg>
        </div>
        <div style="position: absolute; top: -2px; right: -2px; width: 10px; height: 10px; background: #60A5FA; border-radius: 50%; animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;"></div>
      </div>
    `;
    return el;
  }, []);

  // Update marker position during playback
  useEffect(() => {
    if (!map.current || positions.length === 0) return;

    const position = positions[playbackIndex];
    if (!position) return;

    // Create marker if it doesn't exist
    if (!playbackMarker.current) {
      const el = createPlaybackMarkerElement();
      playbackMarker.current = new Marker({ element: el, rotationAlignment: 'map' })
        .setLngLat([position.longitude, position.latitude])
        .addTo(map.current);
    }

    // Update position and rotation
    playbackMarker.current.setLngLat([position.longitude, position.latitude]);
    // Always set rotation (heading 0 = North, increases clockwise)
    playbackMarker.current.setRotation(position.heading || 0);

    // Center on marker during playback
    if (isPlaying) {
      map.current.easeTo({
        center: [position.longitude, position.latitude],
        duration: 200,
      });
    }
  }, [playbackIndex, positions, isPlaying, createPlaybackMarkerElement]);

  // Playback control
  useEffect(() => {
    if (isPlaying && positions.length > 0) {
      playbackIntervalRef.current = setInterval(() => {
        setPlaybackIndex((prev) => {
          if (prev >= positions.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, 1000 / playbackSpeed);
    }

    return () => {
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
      }
    };
  }, [isPlaying, playbackSpeed, positions.length]);

  // Reset playback when device or date range changes (not when trip is selected)
  const prevDeviceIdRef = useRef<string>(selectedDeviceId);
  const prevDateRangeRef = useRef<{ start: Date; end: Date }>(dateRange);

  useEffect(() => {
    // Only reset if device or date range actually changed
    const deviceChanged = prevDeviceIdRef.current !== selectedDeviceId;
    const dateChanged = prevDateRangeRef.current.start.getTime() !== dateRange.start.getTime() ||
                        prevDateRangeRef.current.end.getTime() !== dateRange.end.getTime();

    if (deviceChanged || dateChanged) {
      console.log('Resetting due to device/date change');
      setPlaybackIndex(0);
      setIsPlaying(false);
      setSelectedTrip(null);
      if (playbackMarker.current) {
        playbackMarker.current.remove();
        playbackMarker.current = null;
      }
      prevDeviceIdRef.current = selectedDeviceId;
      prevDateRangeRef.current = dateRange;
    }
  }, [selectedDeviceId, dateRange]);

  // Track last drawn route to avoid redundant redraws
  const lastDrawnRouteRef = useRef<string>('');

  // Redraw route when positions or selectedTrip changes
  useEffect(() => {
    if (!map.current) return;

    // Si aucun trajet sélectionné (positions vides), nettoyer la carte
    if (positions.length === 0) {
      drawRoute(); // drawRoute() nettoie les couches et markers quand positions < 2
      if (playbackMarker.current) {
        playbackMarker.current.remove();
        playbackMarker.current = null;
      }
      lastDrawnRouteRef.current = '';
      return;
    }

    // Create a unique key for this route to avoid redundant redraws
    const routeKey = `${selectedTrip?.startTime || 'all'}-${positions.length}-${positions[0]?.timestamp}`;
    if (lastDrawnRouteRef.current === routeKey) {
      return; // Already drawn this route
    }
    lastDrawnRouteRef.current = routeKey;

    console.log('Positions changed, count:', positions.length, 'selectedTrip:', selectedTrip?.startTime || 'none');

    // Wait for map to be ready
    const redraw = () => {
      if (!map.current) return;
      console.log('Drawing route for', positions.length, 'positions');
      drawRoute();

      // Recreate playback marker at first position after route is drawn
      if (playbackMarker.current) {
        playbackMarker.current.remove();
        playbackMarker.current = null;
      }

      // Create new marker at start position
      if (positions.length > 0) {
        const el = createPlaybackMarkerElement();
        playbackMarker.current = new Marker({ element: el, rotationAlignment: 'map' })
          .setLngLat([positions[0].longitude, positions[0].latitude])
          .setRotation(positions[0].heading || 0)
          .addTo(map.current!);
      }
    };

    if (map.current.loaded()) {
      redraw();
    } else {
      map.current.once('load', redraw);
    }

    return () => {
      map.current?.off('load', redraw);
    };
  }, [positions, selectedTrip, drawRoute, createPlaybackMarkerElement]);

  const handleDatePreset = (preset: typeof datePresets[0]) => {
    const { start, end } = preset.getValue();
    setDateRange({ start, end });
  };

  const handleTripClick = useCallback((trip: Trip) => {
    // Use startTime as unique identifier since trips don't have id from backend
    const tripKey = trip.startTime;
    const isCurrentlySelected = selectedTrip?.startTime === tripKey;

    console.log('handleTripClick:', {
      tripKey,
      isCurrentlySelected,
      allPositionsCount: allPositions.length,
    });

    // If clicking same trip, deselect it (show all positions)
    if (isCurrentlySelected) {
      console.log('Deselecting trip');
      setSelectedTrip(null);
      setPlaybackIndex(0);
      return;
    }

    // Preview filtered positions count
    const startTime = new Date(trip.startTime).getTime();
    const endTime = trip.endTime ? new Date(trip.endTime).getTime() : Date.now();
    const filteredCount = allPositions.filter((p) => {
      const posTime = new Date(p.timestamp).getTime();
      return posTime >= startTime && posTime <= endTime;
    }).length;

    console.log('Selecting trip:', tripKey, 'with', filteredCount, 'positions');

    // Reset playback and select the trip
    setPlaybackIndex(0);
    setIsPlaying(false);
    setSelectedTrip(trip);
    if (isMobile) setMobilePanel('map');
  }, [selectedTrip, allPositions, isMobile]);

  const currentPosition = positions[playbackIndex];
  const selectedDevice = devices.find((d) => d.id === selectedDeviceId);

  // Calculate stats
  const stats = {
    totalDistance: positions.reduce((acc, p, i) => {
      if (i === 0) return 0;
      const prev = positions[i - 1];
      return acc + haversineDistance(prev.latitude, prev.longitude, p.latitude, p.longitude);
    }, 0),
    maxSpeed: Math.max(...positions.map((p) => p.speed), 0),
    avgSpeed: positions.length > 0
      ? positions.reduce((acc, p) => acc + p.speed, 0) / positions.length
      : 0,
    duration: positions.length > 1
      ? new Date(positions[positions.length - 1].timestamp).getTime() -
        new Date(positions[0].timestamp).getTime()
      : 0,
  };

  return (
    <div className={clsx(
      "flex relative",
      isMobile ? "h-[calc(100dvh-8rem)] flex-col" : "h-[calc(100vh-88px)]"
    )}>
      {/* Left Sidebar */}
      <aside className={clsx(
        "bg-white border-r border-gray-200 flex flex-col z-10",
        isMobile
          ? (mobilePanel === 'selection' ? 'flex-1 overflow-hidden' : 'hidden')
          : "w-80"
      )}>
        {/* Header */}
        <div className="p-4 border-b border-white/5">
          <h1 className="text-lg font-semibold text-gray-900 mb-4">{t('history.title')}</h1>

          {/* Device selector */}
          <div className="mb-4">
            <label className="block text-sm text-gray-500 mb-1">{t('history.vehicle')}</label>
            <select
              value={selectedDeviceId}
              onChange={(e) => setSelectedDeviceId(e.target.value)}
              className="w-full bg-gray-50 border border-white/10 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">{t('history.select_vehicle')}</option>
              {devices.map((device) => (
                <option key={device.id} value={device.id}>
                  {device.name} {device.plate && `(${device.plate})`}
                </option>
              ))}
            </select>
          </div>

          {/* Date presets */}
          <div>
            <label className="block text-sm text-gray-500 mb-1">{t('history.period')}</label>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {datePresets.map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => handleDatePreset(preset)}
                  className={clsx(
                    'px-3 py-2 text-sm rounded-lg border transition-colors',
                    dateRange.start.getTime() === preset.getValue().start.getTime()
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'border-white/10 text-gray-600 hover:bg-white/5'
                  )}
                >
                  {t(preset.label)}
                </button>
              ))}
            </div>

            {/* Custom date range */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="text-xs text-gray-500">{t('history.custom_range')}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('history.from')}</label>
                  <input
                    type="date"
                    value={format(dateRange.start, 'yyyy-MM-dd')}
                    onChange={(e) => {
                      const newStart = e.target.value ? startOfDay(new Date(e.target.value)) : dateRange.start;
                      setDateRange(prev => ({ ...prev, start: newStart }));
                    }}
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('history.to')}</label>
                  <input
                    type="date"
                    value={format(dateRange.end, 'yyyy-MM-dd')}
                    onChange={(e) => {
                      const newEnd = e.target.value ? endOfDay(new Date(e.target.value)) : dateRange.end;
                      setDateRange(prev => ({ ...prev, end: newEnd }));
                    }}
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        {selectedDeviceId && positions.length > 0 && (
          <div className="p-4 border-b border-white/5 grid grid-cols-2 gap-3">
            <StatBox icon={Route} label={t('history.stat_distance')} value={`${(stats.totalDistance / 1000).toFixed(1)} km`} />
            <StatBox icon={Clock} label={t('history.stat_duration')} value={formatDuration(stats.duration)} />
            <StatBox icon={Gauge} label={t('history.stat_max_speed')} value={`${Math.round(stats.maxSpeed)} km/h`} color="red" />
            <StatBox icon={Gauge} label={t('history.stat_avg_speed')} value={`${Math.round(stats.avgSpeed)} km/h`} color="green" />
          </div>
        )}

        {/* Trips list */}
        <div className="flex-1 overflow-y-auto relative z-20">
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-medium text-gray-700">
              {t('history.trips_count', { count: trips.length })}
            </h3>
          </div>

          {tripsLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
            </div>
          ) : trips.length === 0 ? (
            <div className="p-8 text-center">
              <Route className="h-10 w-10 mx-auto mb-2 text-gray-400" />
              <p className="text-gray-500 text-sm">{t('history.no_trips')}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {trips.map((trip) => (
                <TripCard
                  key={trip.startTime}
                  trip={trip}
                  isSelected={selectedTrip?.startTime === trip.startTime}
                  onClick={() => handleTripClick(trip)}
                  addresses={tripAddresses.get(trip.startTime)}
                />
              ))}
            </div>
          )}
        </div>

        {isMobile && selectedDeviceId && (
          <div className="p-3 border-t border-gray-200 shrink-0">
            <button
              onClick={() => setMobilePanel('map')}
              className="w-full py-2.5 bg-primary-600 text-white rounded-lg font-medium text-sm hover:bg-primary-500 transition-colors"
            >
              {t('history.see_on_map')}
            </button>
          </div>
        )}
      </aside>

      {/* Map */}
      <div className={clsx(
        "relative bg-gray-100",
        isMobile
          ? (mobilePanel === 'map' ? 'flex-1' : 'hidden')
          : "flex-1"
      )}>
        <div ref={mapContainer} className="absolute inset-0 w-full h-full" />

        {/* Back to trips button (mobile) */}
        {isMobile && (
          <button
            onClick={() => setMobilePanel('selection')}
            className="absolute top-4 left-4 z-10 bg-white px-3 py-2 rounded-lg border border-gray-200 shadow-md flex items-center gap-2 text-sm font-medium text-gray-700"
          >
            <ChevronLeft className="h-4 w-4" />
            Trajets
          </button>
        )}

        {/* Device info overlay */}
        {selectedDevice && (
          <div className={clsx("absolute glass-card p-3", isMobile ? "top-4 right-4" : "top-4 left-4")}>
            <div className="flex items-center gap-3">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: selectedDevice.markerColor || '#3B82F6' }}
              />
              <div>
                <p className="font-medium text-gray-900">{selectedDevice.name}</p>
                {selectedDevice.plate && (
                  <p className="text-xs text-gray-500">{selectedDevice.plate}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Speed legend */}
        {positions.length > 0 && (
          <div className="absolute top-4 right-16 glass-card p-3 hidden md:block">
            <p className="text-xs text-gray-500 mb-2">Vitesse</p>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <span className="text-xs text-gray-600">Lent</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <span className="text-xs text-gray-600">Moyen</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-xs text-gray-600">Rapide</span>
              </div>
            </div>
          </div>
        )}

        {/* Playback controls */}
        {positions.length > 0 && (
          <div className={clsx(
            "absolute left-4 right-4 glass-card",
            isMobile ? "bottom-20 p-3" : "bottom-4 p-4"
          )}>
            {/* Current position info */}
            {currentPosition && (
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-3 md:gap-4 text-sm flex-wrap">
                  <div className="flex items-center gap-1.5 text-gray-600">
                    <Clock className="h-4 w-4 text-primary-400" />
                    {format(new Date(currentPosition.timestamp), 'dd/MM HH:mm:ss', { locale: fr })}
                  </div>
                  <div className="flex items-center gap-1.5 text-gray-600">
                    <Gauge className="h-4 w-4 text-primary-400" />
                    <span className={clsx(
                      'font-medium',
                      currentPosition.speed > 80 ? 'text-red-400' :
                      currentPosition.speed > 50 ? 'text-amber-400' : 'text-emerald-400'
                    )}>
                      {Math.round(currentPosition.speed)} km/h
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-gray-600">
                    <Navigation className="h-4 w-4 text-primary-400" />
                    {currentPosition.heading ? `${Math.round(currentPosition.heading)}°` : '-'}
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  {playbackIndex + 1} / {positions.length}
                </div>
              </div>
            )}

            {/* Progress bar */}
            <div className="relative mb-4">
              <input
                type="range"
                min={0}
                max={positions.length - 1}
                value={playbackIndex}
                onChange={(e) => setPlaybackIndex(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer
                         [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                         [&::-webkit-slider-thumb]:bg-primary-500 [&::-webkit-slider-thumb]:rounded-full
                         [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>
                  {positions.length > 0 &&
                    format(new Date(positions[0].timestamp), 'HH:mm', { locale: fr })}
                </span>
                <span>
                  {positions.length > 0 &&
                    format(new Date(positions[positions.length - 1].timestamp), 'HH:mm', { locale: fr })}
                </span>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => setPlaybackIndex(0)}
                className="p-2 text-gray-500 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                title={t('history.start')}
              >
                <SkipBack className="h-5 w-5" />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className="p-3 bg-primary-600 text-white rounded-full hover:bg-primary-500 transition-colors shadow-lg"
              >
                {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6 ml-0.5" />}
              </button>
              <button
                onClick={() => setPlaybackIndex(positions.length - 1)}
                className="p-2 text-gray-500 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                title={t('history.end')}
              >
                <SkipForward className="h-5 w-5" />
              </button>

              {/* Divider */}
              <div className="w-px h-8 bg-white/10 mx-2 hidden md:block" />

              {/* Speed selector */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 hidden md:inline">Vitesse:</span>
                {[1, 2, 4, 8].map((speed) => (
                  <button
                    key={speed}
                    onClick={() => setPlaybackSpeed(speed)}
                    className={clsx(
                      'px-2.5 py-1 text-sm rounded-lg transition-colors',
                      playbackSpeed === speed
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-500 hover:text-white hover:bg-white/5'
                    )}
                  >
                    {speed}x
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Loading overlay */}
        {positionsLoading && (
          <div className="absolute inset-0 bg-gray-900/50 flex items-center justify-center">
            <div className="glass-card p-6 flex flex-col items-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary-500 mb-2" />
              <p className="text-gray-600">Chargement des positions...</p>
            </div>
          </div>
        )}

        {/* No data message */}
        {!positionsLoading && selectedDeviceId && positions.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="glass-card p-8 text-center">
              <Route className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">Aucune position pour cette période</p>
              <p className="text-sm text-gray-400 mt-1">Essayez une autre plage de dates</p>
            </div>
          </div>
        )}

        {/* No device selected */}
        {!selectedDeviceId && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="glass-card p-8 text-center">
              <MapPin className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">Sélectionnez un véhicule</p>
              <p className="text-sm text-gray-400 mt-1">pour voir son historique de trajets</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatBox({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  color?: 'green' | 'red';
}) {
  return (
    <div className="p-3 bg-white/5 rounded-lg">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={clsx(
          'h-4 w-4',
          color === 'green' ? 'text-emerald-400' :
          color === 'red' ? 'text-red-400' : 'text-primary-400'
        )} />
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <p className="font-semibold text-gray-900">{value}</p>
    </div>
  );
}

function TripCard({
  trip,
  isSelected,
  onClick,
  addresses,
}: {
  trip: Trip;
  isSelected: boolean;
  onClick: () => void;
  addresses?: { start: string; end: string };
}) {
  // Use duration from backend (already in ms)
  const startAddress = addresses?.start || trip.startAddress;
  const endAddress = addresses?.end || trip.endAddress;

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
      className={clsx(
        'w-full text-left p-4 cursor-pointer transition-all duration-200 select-none',
        isSelected
          ? 'bg-blue-100 border-l-4 border-l-blue-500'
          : 'hover:bg-gray-50 border-l-4 border-l-transparent'
      )}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-900">
            {format(new Date(trip.startTime), 'dd/MM/yyyy', { locale: fr })}
          </span>
        </div>
        <span className="text-xs text-gray-500">
          {format(new Date(trip.startTime), 'HH:mm', { locale: fr })}
          {trip.endTime && ` - ${format(new Date(trip.endTime), 'HH:mm', { locale: fr })}`}
          {trip.partLabel && (
            <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded text-[10px] font-medium">
              {trip.partLabel}
            </span>
          )}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 text-xs mb-2">
        <div>
          <p className="text-gray-400">Distance</p>
          <p className="font-medium text-gray-700">{trip.distance.toFixed(1)} km</p>
        </div>
        <div>
          <p className="text-gray-400">Durée</p>
          <p className="font-medium text-gray-700">{formatDuration(trip.duration)}</p>
        </div>
        <div>
          <p className="text-gray-400">Vit. max</p>
          <p className={clsx(
            'font-medium',
            trip.maxSpeed > 130 ? 'text-red-400' : 'text-gray-700'
          )}>
            {Math.round(trip.maxSpeed)} km/h
          </p>
        </div>
      </div>

      {(startAddress || endAddress) && (
        <div className="space-y-1 text-xs">
          <div className="flex items-start gap-2">
            <Flag className="h-3 w-3 mt-0.5 text-emerald-500 flex-shrink-0" />
            <span className="text-gray-500 line-clamp-1">{startAddress || 'Point de depart'}</span>
          </div>
          {endAddress && (
            <div className="flex items-start gap-2">
              <Flag className="h-3 w-3 mt-0.5 text-red-500 flex-shrink-0" />
              <span className="text-gray-500 line-clamp-1">{endAddress}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatDuration(ms: number): string {
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);

  if (hours > 0) {
    return `${hours}h ${minutes}min`;
  }
  return `${minutes}min`;
}

function haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
